import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { useTireInspectionList } from '../../../../stores/tireInspectionStore';
import { fleetApi } from '../../../../api/axiosClient';
import { useLanguage } from '../../../../utils/i18n';

const getFormattedDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const getExportColumns = (t) => [
  { field: 'inspection_date', headerName: t('tireRepairScrap.date', 'Date') },
  { field: 'serial_number', headerName: t('tireRepairScrap.serialNumberExport', 'Serial Number') },
  { field: 'specification', headerName: t('menu.specification', 'Specification') },
  { field: 'vehicle_license_plate', headerName: t('tireCardHistory.vehicle', 'Vehicle') },
  { field: 'position', headerName: t('tireRepairScrap.position', 'Position') },
  { field: 'km_unit', headerName: t('tireRepairScrap.kmUnit', 'KM Unit') },
  { field: 'hm_unit', headerName: t('tireRepairScrap.hmUnit', 'HM Unit') },
  { field: 'total_km', headerName: t('tirePerformance.totalKm', 'Total KM') },
  { field: 'total_hm', headerName: t('tirePerformance.totalHm', 'Total HM') },
  { field: 'air_pressure', headerName: t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)') },
  { field: 'tread_depth', headerName: t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)') },
  { field: 'tire_damage_name', headerName: t('tireCardHistory.tireDamage', 'Damage') },
  { field: 'status_backlog', headerName: t('tireRepairScrap.priority', 'Priority') },
  { field: 'inspector_name', headerName: t('tireRepairScrap.mechanic', 'Inspector Name') },
  { field: 'notes', headerName: t('tireRepairScrap.notes', 'Notes') }
];

const getCellValueForExport = (row, col) => {
  if (col.field === 'inspection_date') {
    return row.inspection_date
      ? dayjs(row.inspection_date).format('YYYY-MM-DD')
      : row.created_date
        ? dayjs(row.created_date).format('YYYY-MM-DD')
        : '-';
  }
  if (col.field === 'specification') {
    return `${row.join?.tire_item_tire_brand_name || '-'} / ${row.join?.tire_item_tire_size_code || '-'} / ${row.join?.tire_item_tire_spec_name || '-'}`;
  }
  if (col.field === 'vehicle_license_plate') {
    return row.join?.vehicle_license_plate || '-';
  }
  return row[col.field] ?? '-';
};

const getStatusColor = (status) => {
  if (!status) return 'default';
  const c = status;
  if (c === 'LOW') return 'default';
  if (c === 'MEDIUM') return 'info';
  if (c === 'HIGH') return 'warning';
  if (c === 'CRITICAL') return 'error';
  return 'default';
};

export default function TireInspectionHistory({ vehicleDetails, companyId, installedSerialNumbers }) {
  const { t } = useLanguage();
  const [historyFilter, setHistoryFilter] = useState('installed'); // 'all' or 'installed'
  const [searchField, setSearchField] = useState('serial_number');
  const [searchValue, setSearchValue] = useState('');

  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportOpen = Boolean(exportAnchorEl);

  const { data: allInspections, isLoading: historyLoading } = useTireInspectionList(
    0,
    9999,
    '',
    '',
    companyId ? [{ value: companyId }] : []
  );

  useEffect(() => {
    setHistoryPage(0);
  }, [installedSerialNumbers, searchValue, searchField, historyFilter]);

  const filteredInspections = useMemo(() => {
    let filtered = Array.isArray(allInspections) ? allInspections : [];

    // 1. Filter by Vehicle / Installed logic
    if (historyFilter === 'installed') {
      filtered = filtered.filter((row) => installedSerialNumbers.includes(row.serial_number));
    } else {
      // 'all': Show all for this vehicle
      const vehicleId = vehicleDetails?.id;
      const licensePlate = vehicleDetails?.license_plate;
      filtered = filtered.filter(
        (row) =>
          (row.vehicle_id && row.vehicle_id == vehicleId) ||
          (row.join?.vehicle_license_plate && row.join?.vehicle_license_plate === licensePlate) ||
          installedSerialNumbers.includes(row.serial_number)
      );
    }

    // Must be history (status backlog empty or undefined or null is considered valid for history in original logic)
    // Wait, original logic: filtered = filtered.filter((row) => row.status_backlog || row.status_backlog.trim() === '');
    // The original logic was weird: row.status_backlog || row.status_backlog.trim() === ''
    // Basically it showed everything for History. We will keep showing everything for history that belongs to this vehicle.

    if (searchValue && searchField) {
      const lowerQ = searchValue.toLowerCase();
      filtered = filtered.filter((row) => {
        const val = row[searchField] || (row.join && row.join[`tire_item_${searchField}`]);
        return (val || '').toString().toLowerCase().includes(lowerQ);
      });
    }
    return filtered;
  }, [allInspections, installedSerialNumbers, searchValue, searchField, historyFilter, vehicleDetails]);

  const [mappedInspections, setMappedInspections] = useState([]);
  const [isMapping, setIsMapping] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchHistoricalData = async () => {
      if (!filteredInspections || filteredInspections.length === 0) {
        if (isMounted) setMappedInspections([]);
        return;
      }
      setIsMapping(true);
      try {
        const uniqueSerials = [...new Set(filteredInspections.map((i) => i.serial_number))].filter(Boolean);
        const payload = {
          paging: { start: 0, length: 9999 },
          orders: { columns: ['created_at'], ascending: false },
          filters: [{ name: 'transaction_type', value: 'INSPECTION' }],
          columns: []
        };
        if (uniqueSerials.length > 0) {
          payload.columns.push({ name: 'serial_number', logic_operator: 'in', operator: 'AND', value: uniqueSerials.join(',') });
        }
        if (companyId) {
          payload.columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(companyId) });
        }

        const tz = Math.abs(new Date().getTimezoneOffset() / 60);
        const resp = await fleetApi.post(`/tire-transactions/search?tz=${tz}`, payload);
        const histData = resp?.data?.data || resp?.data || [];

        const histMap = {};
        histData.forEach((h) => {
          const cTime = dayjs(h.created_at).format('YYYY-MM-DD HH:mm:ss');
          histMap[`${h.serial_number}_${cTime}`] = h;
          const tDate = dayjs(h.transaction_date).format('YYYY-MM-DD');
          histMap[`${h.serial_number}_${tDate}`] = h;
        });

        const newMapped = filteredInspections.map((insp) => {
          const cTime = dayjs(insp.created_date || insp.created_at).format('YYYY-MM-DD HH:mm:ss');
          const keyExact = `${insp.serial_number}_${cTime}`;
          const tDate = dayjs(insp.inspection_date || insp.created_date).format('YYYY-MM-DD');
          const key = `${insp.serial_number}_${tDate}`;
          const hist = histMap[keyExact] || histMap[key];
          return {
            ...insp,
            total_km: hist?.total_km ?? null,
            total_hm: hist?.total_hm ?? null
          };
        });

        if (isMounted) setMappedInspections(newMapped);
      } catch (err) {
        console.error('Failed to map historical data', err);
        if (isMounted) setMappedInspections(filteredInspections);
      } finally {
        if (isMounted) setIsMapping(false);
      }
    };
    fetchHistoricalData();
    return () => {
      isMounted = false;
    };
  }, [filteredInspections, companyId]);

  const inspectionRows = useMemo(() => {
    const start = historyPage * historyRowsPerPage;
    return mappedInspections.slice(start, start + historyRowsPerPage);
  }, [mappedInspections, historyPage, historyRowsPerPage]);

  const totalRecord = mappedInspections.length;

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const fetchAllForExport = useCallback(async () => {
    return mappedInspections;
  }, [mappedInspections]);

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const exportCols = getExportColumns(t);
      const dataToExport = await fetchAllForExport();
      const formattedData = dataToExport.map((row) => {
        const rowValues = {};
        exportCols.forEach((col) => {
          rowValues[col.headerName] = getCellValueForExport(row, col);
        });
        return rowValues;
      });
      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `tire-inspection-history-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const exportCols = getExportColumns(t);
      const dataToExport = await fetchAllForExport();
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Tire Inspection');
      worksheet.columns = exportCols.map((col) => ({
        header: col.headerName,
        key: col.field,
        width: 20
      }));

      dataToExport.forEach((row) => {
        const rowValues = {};
        exportCols.forEach((col) => {
          rowValues[col.field] = getCellValueForExport(row, col);
        });
        worksheet.addRow(rowValues);
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });
      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `tire-inspection-history-${getFormattedDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export Excel:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const exportCols = getExportColumns(t);
      const dataToExport = await fetchAllForExport();
      const doc = new jsPDF('landscape');
      const tableColumn = exportCols.map((col) => col.headerName);
      const tableRows = dataToExport.map((row) => {
        return exportCols.map((col) => {
          const val = getCellValueForExport(row, col);
          return val !== null && val !== undefined ? String(val) : '-';
        });
      });
      doc.setFontSize(16);
      doc.text(t('tireCycle.tireInspectionHistory', 'Tire Inspection History'), 14, 15);
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] }
      });
      doc.save(`tire-inspection-history-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={2}
        >
          {/* Left Side: Toggles & Info */}
          <Box>
            <ToggleButtonGroup
              value={historyFilter}
              exclusive
              onChange={(e, val) => val && setHistoryFilter(val)}
              size="small"
              color="primary"
              sx={{ bgcolor: '#ffffff' }}
            >
              <ToggleButton value="installed" sx={{ textTransform: 'none', px: 3, fontWeight: 600 }}>
                {t('tireCycle.installedTires', 'Installed Tires')}
              </ToggleButton>
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 3, fontWeight: 600 }}>
                {t('tireCycle.allHistory', 'All History')}
              </ToggleButton>
            </ToggleButtonGroup>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
              <InfoOutlinedIcon fontSize="small" color="info" />
              <Typography variant="body2">
                {historyFilter === 'installed'
                  ? t('tireCycle.showingHistoryInstalled', 'Showing history only for tires currently installed on this vehicle.')
                  : t('tireCycle.showingHistoryAll', 'Showing all inspection history for this vehicle, including removed tires.')}
              </Typography>
            </Stack>
          </Box>

          {/* Right Side: Search & Export */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mt: { xs: 2, lg: 0 } }}>
            <FormControl size="small" sx={{ minWidth: 140, bgcolor: '#ffffff', flexGrow: 1 }}>
              <Select value={searchField} onChange={(e) => setSearchField(e.target.value)} displayEmpty>
                <MenuItem value="serial_number">{t('tireRepairScrap.serialNumberExport', 'Serial Number')}</MenuItem>
                <MenuItem value="status_backlog">{t('masterCompany.status', 'Status')}</MenuItem>
                <MenuItem value="condition">{t('dashboardAsset.condition', 'Condition')}</MenuItem>
                <MenuItem value="tire_damage_name">{t('tireRepairScrap.damage', 'Damage')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder={t('dashboardAsset.search', 'Search...')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="small"
              sx={{ minWidth: 180, bgcolor: '#ffffff', flexGrow: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Box>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<DownloadIcon />}
                onClick={handleExportClick}
                disabled={isExporting}
                sx={{ bgcolor: '#ffffff', whiteSpace: 'nowrap' }}
              >
                {isExporting ? t('general.exporting', 'Exporting...') : t('general.export', 'Export')}
              </Button>
              <Menu anchorEl={exportAnchorEl} open={exportOpen} onClose={handleExportClose}>
                <MenuItem onClick={exportToCSV}>{t('tireCycle.exportToCSV', 'Export to CSV')}</MenuItem>
                <MenuItem onClick={exportToExcel}>{t('tireCycle.exportToExcel', 'Export to Excel')}</MenuItem>
                <MenuItem onClick={exportToPDF}>{t('tireCycle.exportToPDF', 'Export to PDF')}</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1000, '& .MuiTableCell-root': { whiteSpace: 'nowrap', px: 2, py: 1.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 5,
                  bgcolor: '#f1f5f9',
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  boxSizing: 'border-box'
                }}
              >
                {t('general.date', 'Date')}
              </TableCell>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 120,
                  zIndex: 5,
                  bgcolor: '#f1f5f9',
                  width: 150,
                  minWidth: 150,
                  maxWidth: 150,
                  boxSizing: 'border-box',
                  borderRight: '2px solid #cbd5e1'
                }}
              >
                {t('tireRepairScrap.serialNumberExport', 'Serial Number')}
              </TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('menu.specification', 'Specification')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('dashboardAsset.vehicle', 'Vehicle')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('masterDriver.position', 'Position')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.kmUnit', 'KM Unit')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.hmUnit', 'HM Unit')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tirePerformance.totalKm', 'Total KM')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tirePerformance.totalHm', 'Total HM')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.tireDamage', 'Damage')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireRepairScrap.priority', 'Priority')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireRepairScrap.mechanic', 'Mechanic')}</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireRepairScrap.notes', 'Notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyLoading || isMapping ? (
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : inspectionRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={15} align="center">
                  {t('tireCycle.noInspectionDataAvailable', 'No inspection data available.')}
                </TableCell>
              </TableRow>
            ) : (
              inspectionRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    bgcolor: '#ffffff',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      bgcolor: '#f8fafc !important',
                      '& .MuiTableCell-root': {
                        bgcolor: '#f8fafc !important'
                      }
                    }
                  }}
                >
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: '#ffffff',
                      transition: 'background-color 0.2s',
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      boxSizing: 'border-box'
                    }}
                  >
                    {row.inspection_date
                      ? dayjs(row.inspection_date).format('YYYY-MM-DD')
                      : row.created_date
                        ? dayjs(row.created_date).format('YYYY-MM-DD')
                        : '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 120,
                      zIndex: 2,
                      bgcolor: '#ffffff',
                      borderRight: '2px solid #cbd5e1',
                      transition: 'background-color 0.2s',
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                      boxSizing: 'border-box'
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {row.serial_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.join?.tire_item_tire_brand_name || '-'} /{' '}
                    {row.join?.tire_item_tire_size_code_internal || row.join?.tire_item_tire_size_code || '-'} /{' '}
                    {row.join?.tire_item_tire_spec_name || '-'}
                  </TableCell>
                  <TableCell>{row.join?.vehicle_license_plate || vehicleDetails?.license_plate || '-'}</TableCell>
                  <TableCell>{row.position || '-'}</TableCell>
                  <TableCell>{row.km_unit ?? '-'}</TableCell>
                  <TableCell>{row.hm_unit ?? '-'}</TableCell>
                  <TableCell>{row.total_km ?? '-'}</TableCell>
                  <TableCell>{row.total_hm ?? '-'}</TableCell>
                  <TableCell>{row.air_pressure ?? '-'}</TableCell>
                  <TableCell>{row.tread_depth ?? '-'}</TableCell>
                  <TableCell>{row.tire_damage_name || '-'}</TableCell>
                  <TableCell>
                    <Chip label={row.status_backlog || '-'} size="small" color={getStatusColor(row.status_backlog)} />
                  </TableCell>
                  <TableCell>{row.inspector_name || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={row.notes || ''}>
                      <span>{row.notes || '-'}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalRecord || 0}
        page={historyPage}
        onPageChange={(e, newPage) => setHistoryPage(newPage)}
        rowsPerPage={historyRowsPerPage}
        onRowsPerPageChange={(e) => {
          setHistoryRowsPerPage(parseInt(e.target.value, 10));
          setHistoryPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 1000]}
      />
    </Box>
  );
}
